import React from 'react';
import Icon from '../../../components/AppIcon';

const TechStackVisualizer = () => {

  const techCategories = {
    cloud: {
      title: 'Cloud',
      icon: 'Cloud',
      color: 'bg-blue-500',
      technologies: [
        {
          name: 'Microsoft Azure',
          description: 'Enterprise-grade cloud and hybrid solutions (AKS, Azure ML, Synapse)',
          logo: 'https://via.placeholder.com/64',
          status: 'Cloud',
          features: ['Azure ML', 'AKS', 'Synapse', 'Cognitive Services']
        },
        {
          name: 'Google Cloud Platform (GCP)',
          description: 'Data and AI platform (Vertex AI, BigQuery, Dataflow, GKE)',
          logo: 'https://via.placeholder.com/64',
          status: 'Cloud',
          features: ['Vertex AI', 'BigQuery', 'Dataflow', 'GKE']
        },
        {
          name: 'Amazon Web Services (AWS)',
          description: 'Wide ecosystem for AI/ML and data platforms',
          logo: 'https://via.placeholder.com/64',
          status: 'Cloud',
          features: ['SageMaker', 'EKS', 'S3', 'Lambda']
        }
      ]
    },
    onprem: {
      title: 'On‑Prem / HPC',
      icon: 'Cpu',
      color: 'bg-emerald-600',
      technologies: [
        {
          name: 'NVIDIA DGX/RTX',
          description: 'High-performance GPU systems for training and inference',
          logo: 'https://via.placeholder.com/64',
          status: 'HPC',
          features: ['DGX', 'RTX', 'PCIe/NVLink', 'NVSwitch']
        },
        {
          name: 'CUDA',
          description: 'GPU compute platform for accelerated ML workloads',
          logo: 'https://via.placeholder.com/64',
          status: 'GPU',
          features: ['cuDNN', 'cuBLAS', 'NCCL', 'RAPIDS']
        },
        {
          name: 'TensorRT & TensorRT‑LLM',
          description: 'High-performance runtime and graph optimization for inference/LLMs',
          logo: 'https://via.placeholder.com/64',
          status: 'Inference',
          features: ['Graph Optimizations', 'Quantization', 'FP16/INT8', 'LLM Kernels']
        },
        {
          name: 'Triton Inference Server',
          description: 'Multi-framework GPU inference serving at scale',
          logo: 'https://via.placeholder.com/64',
          status: 'Serving',
          features: ['Dynamic Batching', 'Ensembles', 'Multi-model', 'Metrics']
        },
        {
          name: 'Slurm / K8s GPU Scheduling',
          description: 'Efficient GPU scheduling with MIG and multi-tenant isolation',
          logo: 'https://via.placeholder.com/64',
          status: 'Scheduler',
          features: ['Slurm', 'Kubernetes', 'MIG', 'NCCL']
        }
      ]
    },
    ai: {
      title: 'AI/ML Frameworks',
      icon: 'Brain',
      color: 'bg-purple-500',
      technologies: [
        {
          name: 'PyTorch',
          description: 'Primary deep learning framework for research and production',
          logo: 'https://via.placeholder.com/64',
          status: 'DL',
          features: ['Distributed', 'TorchServe', 'AMP', 'ONNX']
        },
        {
          name: 'TensorFlow',
          description: 'Production ML with TF Serving and TFX pipelines',
          logo: 'https://via.placeholder.com/64',
          status: 'DL',
          features: ['Keras', 'TF Serving', 'TFX', 'XLA']
        },
        {
          name: 'JAX',
          description: 'High-performance autodiff and TPU/GPU acceleration',
          logo: 'https://via.placeholder.com/64',
          status: 'DL',
          features: ['jit', 'pmap', 'pjit', 'Flax']
        },
        {
          name: 'scikit‑learn',
          description: 'Classic ML for tabular modeling at scale',
          logo: 'https://via.placeholder.com/64',
          status: 'ML',
          features: ['Pipelines', 'Model Selection', 'Feature Engineering']
        },
        {
          name: 'XGBoost',
          description: 'Gradient boosting for high-performance structured data',
          logo: 'https://via.placeholder.com/64',
          status: 'ML',
          features: ['GPU Training', 'Cross Validation', 'Feature Importance']
        }
      ]
    },
    llm: {
      title: 'LLM Serving',
      icon: 'Layers',
      color: 'bg-cyan-600',
      technologies: [
        {
          name: 'vLLM',
          description: 'High-throughput LLM serving with PagedAttention',
          logo: 'https://via.placeholder.com/64',
          status: 'Serving',
          features: ['PagedAttention', 'Tensor Parallel', 'OpenAI API']
        },
        {
          name: 'Text Generation Inference (TGI)',
          description: 'Optimized LLM server by Hugging Face',
          logo: 'https://via.placeholder.com/64',
          status: 'Serving',
          features: ['Speculative Decoding', 'Quantization', 'OpenAPI']
        },
        {
          name: 'Ollama (lab)',
          description: 'Local model runner for experiments and labs',
          logo: 'https://via.placeholder.com/64',
          status: 'Lab',
          features: ['Local Serving', 'Model Zoo', 'Dev Tooling']
        },
        {
          name: 'TensorRT‑LLM',
          description: 'NVIDIA-optimized LLM kernels for maximum throughput',
          logo: 'https://via.placeholder.com/64',
          status: 'Inference',
          features: ['FP8/FP16', 'Tensor Parallel', 'Plugin Kernels']
        },
        {
          name: 'Triton (LLM)',
          description: 'LLM serving via Triton Inference Server',
          logo: 'https://via.placeholder.com/64',
          status: 'Serving',
          features: ['Dynamic Batching', 'Multi-model', 'Metrics']
        }
      ]
    },
    tooling: {
      title: 'LLM Tooling',
      icon: 'Server',
      color: 'bg-teal-600',
      technologies: [
        {
          name: 'LangChain',
          description: 'Framework for RAG, agents, and LLM apps',
          logo: 'https://via.placeholder.com/64',
          status: 'Tooling',
          features: ['RAG', 'Agents', 'Memory', 'Tools']
        },
        {
          name: 'LlamaIndex',
          description: 'Data framework for LLMs and RAG pipelines',
          logo: 'https://via.placeholder.com/64',
          status: 'Tooling',
          features: ['Indexing', 'Retrievers', 'Routing', 'Evaluations']
        },
        {
          name: 'OpenAI / Azure OpenAI',
          description: 'Hosted LLM APIs and enterprise controls',
          logo: 'https://via.placeholder.com/64',
          status: 'API',
          features: ['GPT Models', 'Guardrails', 'Monitoring']
        },
        {
          name: 'Hugging Face',
          description: 'Models, datasets, Spaces, and Inference Endpoints',
          logo: 'https://via.placeholder.com/64',
          status: 'Platform',
          features: ['Transformers', 'Datasets', 'Inference', 'Hub']
        }
      ]
    },
    data_orchestration: {
      title: 'Data & Orchestration',
      icon: 'Database',
      color: 'bg-indigo-600',
      technologies: [
        {
          name: 'Databricks',
          description: 'Lakehouse platform for data engineering and ML',
          logo: 'https://via.placeholder.com/64',
          status: 'Platform',
          features: ['Delta Lake', 'MLflow', 'Unity Catalog', 'Photon']
        },
        {
          name: 'Apache Spark',
          description: 'Distributed compute engine for ETL and ML at scale',
          logo: 'https://via.placeholder.com/64',
          status: 'Compute',
          features: ['Spark SQL', 'Structured Streaming', 'MLlib']
        },
        {
          name: 'Snowflake',
          description: 'Cloud data platform and warehouse',
          logo: 'https://via.placeholder.com/64',
          status: 'Warehouse',
          features: ['Snowpark', 'Streams/Tasks', 'MarketPlace']
        },
        {
          name: 'BigQuery',
          description: 'Serverless analytics warehouse on GCP',
          logo: 'https://via.placeholder.com/64',
          status: 'Warehouse',
          features: ['BigQuery ML', 'BI Engine', 'Federation']
        },
        {
          name: 'Azure Synapse',
          description: 'Unified analytics and warehousing on Azure',
          logo: 'https://via.placeholder.com/64',
          status: 'Warehouse',
          features: ['Pipelines', 'Serverless SQL', 'Spark']
        },
        {
          name: 'Airflow / Argo / dbt / Kafka',
          description: 'Workflow orchestration and data transformation/streaming',
          logo: 'https://via.placeholder.com/64',
          status: 'Orchestration',
          features: ['Airflow', 'Argo', 'dbt', 'Kafka/Kinesis/PubSub']
        },
        {
          name: 'Lakehouse Formats',
          description: 'Open table formats for the data lakehouse',
          logo: 'https://via.placeholder.com/64',
          status: 'Storage',
          features: ['Delta', 'Iceberg', 'Hudi']
        }
      ]
    },
    vector_search: {
      title: 'Vector & Search',
      icon: 'Search',
      color: 'bg-orange-600',
      technologies: [
        {
          name: 'Pinecone',
          description: 'Managed vector database for semantic search',
          logo: 'https://via.placeholder.com/64',
          status: 'Vector DB',
          features: ['Namespaces', 'Filters', 'Indexing']
        },
        {
          name: 'Weaviate',
          description: 'Open-source vector database with hybrid search',
          logo: 'https://via.placeholder.com/64',
          status: 'Vector DB',
          features: ['Modules', 'Filters', 'Hybrid Search']
        },
        {
          name: 'Milvus',
          description: 'Scalable vector database for RAG systems',
          logo: 'https://via.placeholder.com/64',
          status: 'Vector DB',
          features: ['HNSW', 'GPU Accel', 'Sharding']
        },
        {
          name: 'pgvector',
          description: 'Vector extension for PostgreSQL',
          logo: 'https://via.placeholder.com/64',
          status: 'Extension',
          features: ['IVFFlat', 'HNSW', 'Indexing']
        },
        {
          name: 'Redis',
          description: 'Real-time search with Redis Stack',
          logo: 'https://via.placeholder.com/64',
          status: 'Cache/DB',
          features: ['HNSW', 'TTL', 'Streams']
        },
        {
          name: 'Elasticsearch / OpenSearch',
          description: 'Full-text and vector search platforms',
          logo: 'https://via.placeholder.com/64',
          status: 'Search',
          features: ['BM25', 'KNN', 'Aggregations']
        }
      ]
    },
    devops_security: {
      title: 'DevOps & Security',
      icon: 'Shield',
      color: 'bg-rose-600',
      technologies: [
        {
          name: 'Kubernetes / Helm / Terraform',
          description: 'Platform orchestration and infrastructure as code',
          logo: 'https://via.placeholder.com/64',
          status: 'Platform',
          features: ['K8s', 'Helm', 'Terraform']
        },
        {
          name: 'GitHub/GitLab CI',
          description: 'CI/CD for reliable and secure delivery',
          logo: 'https://via.placeholder.com/64',
          status: 'CI/CD',
          features: ['Pipelines', 'Runners', 'Policies']
        },
        {
          name: 'Vault / KMS / HSM',
          description: 'Secrets, keys, and hardware-backed security',
          logo: 'https://via.placeholder.com/64',
          status: 'Security',
          features: ['PKI', 'KMS', 'HSM']
        },
        {
          name: 'Observability',
          description: 'Metrics, logs, traces, and APM',
          logo: 'https://via.placeholder.com/64',
          status: 'Observability',
          features: ['Prometheus', 'Grafana', 'ELK/EFK', 'APM']
        },
        {
          name: 'Model Observability & Evals',
          description: 'Monitoring and evaluation for ML systems',
          logo: 'https://via.placeholder.com/64',
          status: 'MLOps',
          features: ['MLflow', 'Weights & Biases', 'LangSmith', 'TruLens', 'Evidently', 'Arize']
        }
      ]
    },
    datastores: {
      title: 'Datastores',
      icon: 'Database',
      color: 'bg-amber-600',
      technologies: [
        {
          name: 'PostgreSQL',
          description: 'Relational database for transactional workloads',
          logo: 'https://via.placeholder.com/64',
          status: 'RDBMS',
          features: ['ACID', 'JSONB', 'Replication']
        },
        {
          name: 'MySQL / MariaDB',
          description: 'Widely adopted relational datastore',
          logo: 'https://via.placeholder.com/64',
          status: 'RDBMS',
          features: ['InnoDB', 'Replication', 'Partitioning']
        },
        {
          name: 'Microsoft SQL Server',
          description: 'Enterprise relational database',
          logo: 'https://via.placeholder.com/64',
          status: 'RDBMS',
          features: ['Columnstore', 'HA/DR', 'Security']
        },
        {
          name: 'MongoDB',
          description: 'Document database with flexible schema',
          logo: 'https://via.placeholder.com/64',
          status: 'NoSQL',
          features: ['Sharding', 'Aggregation', 'TTL']
        },
        {
          name: 'Graphs: Neo4j / Neptune',
          description: 'Graph databases for connected data',
          logo: 'https://via.placeholder.com/64',
          status: 'Graph',
          features: ['Cypher/Gremlin', 'Graph Algorithms', 'ACID']
        },
        {
          name: 'Object Storage',
          description: 'Durable and scalable data lake storage',
          logo: 'https://via.placeholder.com/64',
          status: 'Storage',
          features: ['S3', 'ADLS', 'GCS']
        }
      ]
    }
  };

  const getAcronym = (name) => {
    if (!name) return 'AI';
    const paren = name.match(/\(([^)]+)\)/);
    if (paren && paren[1]) {
      const inside = paren[1].replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      if (inside.length >= 2 && inside.length <= 5) return inside.slice(0, 4);
    }
    const cleaned = name.replace(/\([^)]*\)/g, ' ').replace(/[^A-Za-z0-9+ ]/g, ' ');
    const parts = cleaned.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'AI';
    if (parts.length === 1) {
      const token = parts[0].replace(/[^A-Za-z0-9]/g, '');
      return token.slice(0, 3).toUpperCase();
    }
    return parts.slice(0, 3).map((p) => p[0].toUpperCase()).join('');
  };

  const categoryOrder = [
    'cloud',
    'onprem',
    'ai',
    'llm',
    'tooling',
    'data_orchestration',
    'vector_search',
    'devops_security',
    'datastores',
  ];

  return (
    <div className="space-y-10">
      {/* Quick jump pills */}
      <div className="flex flex-wrap gap-2 mb-2">
        {categoryOrder.map((key) => (
          <a key={key} href={`#${key}`} className="px-3 py-1 rounded-full text-xs bg-white/10 text-white/80 border border-white/15 hover:bg-white/15 enterprise-transition">
            {techCategories[key]?.title}
          </a>
        ))}
      </div>

      {categoryOrder.map((key) => {
        const category = techCategories[key];
        if (!category) return null;
        return (
          <section key={key} id={key} className="blue-radial-bg glass-panel rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
                  <Icon name={category?.icon} size={18} />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{category?.title}</h3>
              </div>
              <span className={`hidden sm:inline px-2 py-1 text-xs rounded-full ${category?.color} text-white/90`}>Category</span>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category?.technologies?.map((tech, index) => (
                  <div key={index} className="glass-card rounded-xl p-6 enterprise-transition group">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-white/10 border border-white/15 flex items-center justify-center">
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-sky-400/25 to-indigo-400/25 text-white font-semibold">
                          <span className="text-sm">{getAcronym(tech?.name)}</span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-foreground group-hover:text-innovation-blue enterprise-transition">
                            {tech?.name}
                          </h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${category?.color} text-white/90`}>
                            {tech?.status}
                          </span>
                        </div>
                        <p className="text-sm text-white/70 mb-3 leading-relaxed">{tech?.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {tech?.features?.map((feature, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-white/10 border border-white/20 rounded-full text-white/80">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default TechStackVisualizer;